import { useState, useEffect, useMemo } from 'react';
import { useLanguage } from '../../context/LanguageContext';
import { useAuth } from '../../context/AuthContext';
import API from '../../utils/api';
import PageHeader from '../../components/common/PageHeader';
import EmptyState from '../../components/common/EmptyState';
import Badge from '../../components/common/Badge';
import Modal from '../../components/common/Modal';
import {
  HiOutlinePlus,
  HiOutlinePencil,
  HiOutlineTrash,
  HiOutlineX,
  HiOutlineSearch,
  HiOutlineEye,
  HiOutlineCheck,
  HiOutlineExclamation,
  HiOutlineCash,
  HiOutlineCalendar,
  HiOutlineUser,
  HiOutlineShieldCheck,
  HiOutlineBan,
  HiOutlineRefresh,
  HiOutlineClock,
} from 'react-icons/hi';

const fmtBDT = (n) => `৳${Number(n || 0).toLocaleString('en-IN')}`;
const fmtDays = (n) => `${Number(n || 0).toFixed(1)} দিন`;

const STATUS_LABEL = {
  present: 'উপস্থিত', absent: 'অনুপস্থিত', half_day: 'আধা দিন',
  pending: 'বকেয়া', paid: 'পরিশোধিত', partial: 'আংশিক', approved: 'অনুমোদিত', rejected: 'প্রত্যাখ্যাত',
};
const STATUS_VARIANT = {
  present: 'success', absent: 'danger', half_day: 'warning',
  pending: 'warning', paid: 'success', partial: 'warning',
  approved: 'success', rejected: 'danger',
};

const WorkersPage = () => {
  const [workers, setWorkers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('all'); // all | admin | worker
  const [activeFilter, setActiveFilter] = useState('all'); // all | active | inactive

  // Form + edit modal
  const [formOpen, setFormOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [formData, setFormData] = useState({
    name: '', email: '', phone: '', password: '', daily_salary: 400, role: 'worker',
  });
  const [formSaving, setFormSaving] = useState(false);
  const [formError, setFormError] = useState('');

  // Profile drawer (per-worker history)
  const [profileOpen, setProfileOpen] = useState(false);
  const [profile, setProfile] = useState(null);
  const [profileLoading, setProfileLoading] = useState(false);

  // WhatsApp report
  const [reportOpen, setReportOpen] = useState(false);
  const [report, setReport] = useState(null);
  const [reportLoading, setReportLoading] = useState(false);
  const [reportPeriod, setReportPeriod] = useState('4w');

  // Delete confirmation
  const [confirmDelete, setConfirmDelete] = useState(null); // { kind: 'soft'|'hard', worker }
  const [deleteText, setDeleteText] = useState('');
  const [deleting, setDeleting] = useState(false);

  const { t } = useLanguage();
  const { user: currentUser } = useAuth();

  useEffect(() => { fetchWorkers(); }, []);

  const fetchWorkers = async () => {
    setLoading(true);
    try {
      const { data } = await API.get('/admin/workers');
      setWorkers(data.workers);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const filtered = useMemo(() => {
    const q = search.toLowerCase().trim();
    return workers
      .filter((w) => roleFilter === 'all' ? true : w.role === roleFilter)
      .filter((w) => activeFilter === 'all' ? true : activeFilter === 'active' ? w.is_active : !w.is_active)
      .filter((w) => !q ? true : (w.name || '').toLowerCase().includes(q) || (w.phone || '').includes(q) || (w.email || '').toLowerCase().includes(q));
  }, [workers, search, roleFilter, activeFilter]);

  const adminCount    = workers.filter((w) => w.role === 'admin').length;
  const activeCount   = workers.filter((w) => w.is_active).length;
  const inactiveCount = workers.length - activeCount;
  const workerCount   = workers.filter((w) => w.role === 'worker').length;

  // ── Form handlers ──────────────────────────────────────────────────────
  const openCreate = () => {
    setEditId(null);
    setFormData({ name: '', email: '', phone: '', password: '', daily_salary: 400, role: 'worker' });
    setFormError('');
    setFormOpen(true);
  };

  const openEdit = (w) => {
    setEditId(w.id);
    setFormData({
      name: w.name || '',
      email: w.email || '',
      phone: w.phone || '',
      password: '',
      daily_salary: w.daily_salary || 400,
      role: w.role,
    });
    setFormError('');
    setFormOpen(true);
  };

  const submitForm = async (e) => {
    e.preventDefault();
    setFormSaving(true);
    setFormError('');
    try {
      const payload = { ...formData };
      // For edit, only send password if the admin typed one
      if (editId && !payload.password) delete payload.password;
      // Empty phone → null
      if (!payload.phone) payload.phone = null;
      if (editId) {
        await API.put(`/admin/workers/${editId}`, payload);
      } else {
        if (!payload.password) {
          setFormError('পাসওয়ার্ড আবশ্যক।');
          setFormSaving(false);
          return;
        }
        await API.post('/admin/workers', payload);
      }
      setFormOpen(false);
      fetchWorkers();
    } catch (e) {
      setFormError(e.response?.data?.message || 'সার্ভার ত্রুটি।');
    } finally {
      setFormSaving(false);
    }
  };

  // ── Active toggle ──────────────────────────────────────────────────────
  const toggleActive = async (w) => {
    try {
      await API.put(`/admin/workers/${w.id}/active`);
      fetchWorkers();
      if (profileOpen && profile?.user?.id === w.id) {
        // Refresh profile if currently viewing
        openProfile(w);
      }
    } catch (e) {
      alert(e.response?.data?.message || 'সার্ভার ত্রুটি।');
    }
  };

  // ── Delete handlers ────────────────────────────────────────────────────
  const performDelete = async () => {
    if (!confirmDelete) return;
    setDeleting(true);
    try {
      const targetId = confirmDelete.worker.id;
      if (confirmDelete.kind === 'soft') {
        await API.delete(`/admin/workers/${targetId}`);
      } else {
        await API.delete(`/admin/workers/${targetId}/hard`);
      }
      setConfirmDelete(null);
      setDeleteText('');
      // If the deleted user is currently open in the profile drawer, close
      // it too so the UI doesn't get stuck showing a vanished user.
      if (profileOpen && profile?.user?.id === targetId) {
        setProfileOpen(false);
        setProfile(null);
      }
      fetchWorkers();
    } catch (e) {
      alert(e.response?.data?.message || 'সার্ভার ত্রুটি।');
    } finally {
      setDeleting(false);
    }
  };

  // ── Profile drawer ─────────────────────────────────────────────────────
  const openProfile = async (w) => {
    setProfileOpen(true);
    setProfile(null);
    setProfileLoading(true);
    try {
      const { data } = await API.get(`/admin/workers/${w.id}/details`);
      setProfile(data);
    } catch (e) {
      setProfileOpen(false);
    } finally {
      setProfileLoading(false);
    }
  };

  const openReport = async (w, period = '4w') => {
    setReportPeriod(period);
    setReportOpen(true);
    setReport(null);
    setReportLoading(true);
    try {
      const { data } = await API.get(`/admin/workers/${w.id}/report?period=${period}`);
      setReport(data);
    } catch (e) {
      setReportOpen(false);
    } finally {
      setReportLoading(false);
    }
  };

  const buildWhatsAppMessage = (r) => {
    if (!r) return '';
    const phone = r.worker.phone;
    const w = r.worker;
    const a = r.attendance;
    const s = r.salary;
    const adv = r.advance;
    const fmt = (n) => `৳${Number(n || 0).toLocaleString('en-IN')}`;
    const topSite = a.sites?.[0]?.name || '—';

    return `📊 *${w.name}* (${phone}) এর রিপোর্ট
📅 ${r.period.from} → ${r.period.to}

✅ *কাজের দিন:* ${a.total_days} (প্রেজেন্ট ${a.present}, হাফ ${a.half})
📍 *সাইট:* ${topSite}${a.sites.length > 1 ? ` +${a.sites.length - 1}` : ''}
${a.sites.length > 0 ? a.sites.slice(0, 3).map((s) => `   • ${s.name} — ${s.days} দিন`).join('\n') : ''}

💰 *মোট আয়:* ${fmt(s.gross)}
💸 *অগ্রিম কর্তন:* ${fmt(s.advance)}
✅ *পরিশোধিত:* ${fmt(s.paid)}
⏳ *বকেয়া:* ${fmt(s.pending)}

${adv.approved > 0 ? `📌 *অগ্রিম নিয়েছেন:* ${fmt(adv.approved)}` : ''}${adv.pending > 0 ? ` (পেন্ডিং: ${fmt(adv.pending)})` : ''}

📆 *এই সপ্তাহের আনুমানিক বাকি:* ${fmt(r.current_week_estimate)}

— kaj.bro.bd`;
  };

  const shareToWhatsApp = (r) => {
    if (!r) return;
    const msg = buildWhatsAppMessage(r);
    const phone = r.worker.phone.replace(/[^0-9]/g, '');
    const bdPhone = phone.startsWith('0') ? '880' + phone.slice(1) : phone;
    const url = `https://wa.me/${bdPhone}?text=${encodeURIComponent(msg)}`;
    window.open(url, '_blank');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-10 h-10 border-4 border-brand-200 border-brand-600 rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <PageHeader
        title="ওয়ার্কার"
        subtitle={`মোট ${workers.length} জন — অ্যাডমিন ${adminCount}, ওয়ার্কার ${workerCount}, সক্রিয় ${activeCount}, নিষ্ক্রিয় ${inactiveCount}`}
        action={
          <button onClick={openCreate} className="btn-primary">
            <HiOutlinePlus className="w-5 h-5" />
            নতুন ইউজার
          </button>
        }
      />

      {/* Search + filter bar */}
      <div className="card">
        <div className="flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
          <div className="relative flex-1 min-w-0">
            <HiOutlineSearch className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-ink-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="🔍 নাম, ফোন বা ইমেইল খুঁজুন..."
              className="input-field pl-10"
            />
            {search && (
              <button onClick={() => setSearch('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-ink-400 hover:text-ink-700">
                <HiOutlineX className="w-4 h-4" />
              </button>
            )}
          </div>
          <div className="flex gap-1 p-1 bg-ink-100 dark:bg-ink-800 rounded-xl">
            {[
              { v: 'all', label: `সব (${workers.length})` },
              { v: 'admin', label: `অ্যাডমিন (${adminCount})` },
              { v: 'worker', label: `ওয়ার্কার (${workerCount})` },
            ].map((tab) => (
              <button
                key={tab.v}
                onClick={() => setRoleFilter(tab.v)}
                className={
                  'px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ' +
                  (roleFilter === tab.v
                    ? 'bg-white dark:bg-ink-700 text-brand-700 dark:text-brand-300 shadow-sm'
                    : 'text-ink-600 dark:text-ink-300 hover:text-ink-900 dark:hover:text-white')
                }
              >
                {tab.label}
              </button>
            ))}
          </div>
          <div className="flex gap-1 p-1 bg-ink-100 dark:bg-ink-800 rounded-xl">
            {[
              { v: 'all', label: `সব (${workers.length})` },
              { v: 'active', label: `সক্রিয় (${activeCount})` },
              { v: 'inactive', label: `নিষ্ক্রিয় (${inactiveCount})` },
            ].map((tab) => (
              <button
                key={tab.v}
                onClick={() => setActiveFilter(tab.v)}
                className={
                  'px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ' +
                  (activeFilter === tab.v
                    ? 'bg-white dark:bg-ink-700 text-brand-700 dark:text-brand-300 shadow-sm'
                    : 'text-ink-600 dark:text-ink-300 hover:text-ink-900 dark:hover:text-white')
                }
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Workers table */}
      <div className="card p-0 overflow-hidden">
        {filtered.length === 0 ? (
          <EmptyState icon="👷" title="কোনো ইউজার নেই" description="নতুন ইউজার বাটনে ক্লিক করে যোগ করুন।" />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead className="bg-ink-50 dark:bg-ink-900/30 border-y border-ink-100 dark:border-ink-700">
                <tr>
                  <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wider text-ink-500 dark:text-ink-400">নাম</th>
                  <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wider text-ink-500 dark:text-ink-400">যোগাযোগ</th>
                  <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wider text-ink-500 dark:text-ink-400 text-center">দৈনিক বেতন</th>
                  <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wider text-ink-500 dark:text-ink-400 text-center">Role</th>
                  <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wider text-ink-500 dark:text-ink-400 text-center">Status</th>
                  <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wider text-ink-500 dark:text-ink-400 text-right">অ্যাকশন</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-ink-100 dark:divide-ink-700">
                {filtered.map((w) => {
                  const isMe = currentUser?.id === w.id;
                  const canDelete = !isMe || (isMe && adminCount > 1);
                  return (
                    <tr key={w.id} className="hover:bg-ink-50 dark:hover:bg-ink-900/30 transition-colors">
                      <td className="px-5 py-3">
                        <button onClick={() => openProfile(w)} className="flex items-center gap-3 text-left group">
                          <div className={
                            'w-10 h-10 rounded-xl flex items-center justify-center text-white font-bold ' +
                            (w.role === 'admin' ? 'bg-gradient-to-br from-amber-500 to-amber-700' : 'bg-gradient-brand')
                          }>
                            {w.name?.charAt(0).toUpperCase()}
                          </div>
                          <div>
                            <div className="font-semibold text-ink-900 dark:text-white flex items-center gap-1.5 group-hover:text-brand-600 transition-colors">
                              {w.name}
                              {isMe && <span className="text-[10px] font-bold uppercase text-brand-700 bg-brand-100 dark:bg-brand-900/40 px-1.5 py-0.5 rounded">আপনি</span>}
                            </div>
                            <div className="text-xs text-ink-500 dark:text-ink-400">ID #{w.id}</div>
                          </div>
                        </button>
                      </td>
                      <td className="px-5 py-3 text-sm">
                        <div className="font-mono text-ink-700 dark:text-ink-300">{w.phone || '—'}</div>
                        <div className="text-xs text-ink-500 dark:text-ink-400 truncate max-w-[200px]">{w.email || '—'}</div>
                      </td>
                      <td className="px-5 py-3 text-sm text-center text-ink-700 dark:text-ink-300 font-medium">
                        {fmtBDT(w.daily_salary)}
                      </td>
                      <td className="px-5 py-3 text-center">
                        <select
                          value={w.role}
                          disabled={isMe && w.role === 'admin' && adminCount === 1}
                          onChange={async (e) => {
                            try {
                              await API.put(`/admin/workers/${w.id}/role`, { role: e.target.value });
                              fetchWorkers();
                            } catch (err) { alert(err.response?.data?.message || 'ত্রুটি'); }
                          }}
                          className={
                            'text-xs font-semibold px-2 py-1 rounded-lg border-0 cursor-pointer ' +
                            (w.role === 'admin'
                              ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200'
                              : 'bg-brand-100 text-brand-800 dark:bg-brand-900/40 dark:text-brand-200')
                          }
                        >
                          <option value="worker">👷 ওয়ার্কার</option>
                          <option value="admin">👑 অ্যাডমিন</option>
                        </select>
                      </td>
                      <td className="px-5 py-3 text-center">
                        <button
                          type="button"
                          onClick={() => toggleActive(w)}
                          disabled={isMe && w.role === 'admin' && adminCount === 1 && w.is_active}
                          className={
                            'inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold transition-colors disabled:cursor-not-allowed ' +
                            (w.is_active
                              ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200 hover:bg-emerald-200 dark:hover:bg-emerald-900/60'
                              : 'bg-ink-200 text-ink-600 dark:bg-ink-700 dark:text-ink-300 hover:bg-ink-300 dark:hover:bg-ink-600')
                          }
                          title="ক্লিক করে সক্রিয়/নিষ্ক্রিয় toggle করুন"
                        >
                          <span className={'w-2 h-2 rounded-full ' + (w.is_active ? 'bg-emerald-500' : 'bg-ink-500')} />
                          {w.is_active ? 'সক্রিয়' : 'নিষ্ক্রিয়'}
                        </button>
                      </td>
                      <td className="px-5 py-3">
                        <div className="flex items-center justify-end gap-1">
                          {w.role === 'worker' && w.phone && (
                            <button
                              onClick={() => openReport(w)}
                              className="p-2 rounded-lg text-emerald-600 hover:bg-emerald-100 dark:hover:bg-emerald-900/40 transition-colors"
                              title="WhatsApp এ রিপোর্ট পাঠান"
                            >
                              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                              </svg>
                            </button>
                          )}
                          <button
                            onClick={() => openProfile(w)}
                            className="p-2 rounded-lg text-brand-600 hover:bg-brand-100 dark:hover:bg-brand-900/40 transition-colors"
                            title="বিস্তারিত দেখুন"
                          >
                            <HiOutlineEye className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => openEdit(w)}
                            className="p-2 rounded-lg text-ink-600 hover:bg-ink-100 dark:hover:bg-ink-700 transition-colors"
                            title="সম্পাদনা"
                          >
                            <HiOutlinePencil className="w-4 h-4" />
                          </button>
                          {canDelete && w.is_active && (
                            <button
                              onClick={() => setConfirmDelete({ kind: 'soft', worker: w })}
                              className="p-2 rounded-lg text-amber-600 hover:bg-amber-100 dark:hover:bg-amber-900/40 transition-colors"
                              title="নিষ্ক্রিয় করুন (deactivate)"
                            >
                              <HiOutlineBan className="w-4 h-4" />
                            </button>
                          )}
                          {canDelete && (
                            <button
                              onClick={() => setConfirmDelete({ kind: 'hard', worker: w })}
                              className="p-2 rounded-lg text-rose-600 hover:bg-rose-100 dark:hover:bg-rose-900/40 transition-colors"
                              title="সম্পূর্ণ মুছুন (hard delete)"
                            >
                              <HiOutlineTrash className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ── Form modal (create + edit) ───────────────────────────────────── */}
      <Modal
        isOpen={formOpen}
        onClose={() => setFormOpen(false)}
        title={editId ? `✏️ সম্পাদনা: ${formData.name || ''}` : 'নতুন ইউজার'}
      >
        <form onSubmit={submitForm} className="space-y-4">
          {formError && (
            <div className="p-3 bg-rose-50 dark:bg-rose-900/30 border border-rose-200 dark:border-rose-800 rounded-xl text-rose-700 dark:text-rose-200 text-sm flex items-start gap-2">
              <HiOutlineExclamation className="w-5 h-5 flex-shrink-0 mt-0.5" />
              <span>{formError}</span>
            </div>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="input-label">নাম <span className="text-rose-500">*</span></label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="input-field"
                required
              />
            </div>
            <div>
              <label className="input-label">ফোন</label>
              <input
                type="tel"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                className="input-field"
                placeholder="01XXXXXXXXX"
              />
            </div>
            <div>
              <label className="input-label">ইমেইল</label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="input-field"
              />
            </div>
            <div>
              <label className="input-label">
                পাসওয়ার্ড {editId && <span className="text-xs text-ink-500">(খালি রাখলে পরিবর্তন হবে না)</span>}
                {!editId && <span className="text-rose-500"> *</span>}
              </label>
              <input
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                className="input-field"
                required={!editId}
                placeholder={editId ? '••••••••' : ''}
              />
            </div>
            <div>
              <label className="input-label">দৈনিক বেতন (৳)</label>
              <input
                type="number"
                value={formData.daily_salary}
                onChange={(e) => setFormData({ ...formData, daily_salary: Number(e.target.value) })}
                className="input-field"
                min="0"
              />
            </div>
            <div>
              <label className="input-label">Role</label>
              <div className="grid grid-cols-2 gap-2">
                {[
                  { v: 'worker', label: '👷 ওয়ার্কার', color: 'brand' },
                  { v: 'admin',  label: '👑 অ্যাডমিন',  color: 'amber' },
                ].map((opt) => (
                  <button
                    key={opt.v}
                    type="button"
                    onClick={() => setFormData({ ...formData, role: opt.v })}
                    className={
                      'px-3 py-2 rounded-xl text-sm font-semibold border transition-colors ' +
                      (formData.role === opt.v
                        ? opt.color === 'brand'
                          ? 'bg-brand-50 border-brand-500 text-brand-700 dark:bg-brand-900/40 dark:text-brand-200'
                          : 'bg-amber-50 border-amber-500 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200'
                        : 'bg-white dark:bg-ink-800 border-ink-200 dark:border-ink-700 text-ink-700 dark:text-ink-300')
                    }
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-3 border-t border-ink-100 dark:border-ink-700">
            <button type="button" onClick={() => setFormOpen(false)} className="btn-secondary">বাতিল</button>
            <button type="submit" disabled={formSaving} className="btn-primary">
              {formSaving ? <HiOutlineRefresh className="w-4 h-4 animate-spin" /> : <HiOutlineCheck className="w-4 h-4" />}
              {editId ? 'আপডেট করুন' : 'তৈরি করুন'}
            </button>
          </div>
        </form>
      </Modal>

      {/* ── Profile drawer ─────────────────────────────────────────────────── */}
      <Modal
        isOpen={profileOpen}
        onClose={() => setProfileOpen(false)}
        title={profile?.user?.name || 'প্রোফাইল'}
        size="xl"
      >
        {profileLoading ? (
          <div className="flex items-center justify-center py-12">
            <div className="w-10 h-10 border-4 border-brand-200 border-t-brand-600 rounded-full animate-spin" />
          </div>
        ) : !profile ? null : (
          <div className="space-y-4">
            {/* Header card */}
            <div className={
              'p-4 rounded-2xl flex items-center gap-4 border ' +
              (profile.user.role === 'admin'
                ? 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800'
                : 'bg-brand-50 dark:bg-brand-900/20 border-brand-200 dark:border-brand-800')
            }>
              <div className={
                'w-16 h-16 rounded-2xl flex items-center justify-center text-white font-bold text-2xl ' +
                (profile.user.role === 'admin' ? 'bg-gradient-to-br from-amber-500 to-amber-700' : 'bg-gradient-brand')
              }>
                {profile.user.name?.charAt(0).toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-xl font-bold text-ink-900 dark:text-white truncate">{profile.user.name}</h3>
                <p className="text-sm text-ink-500 dark:text-ink-400">
                  {profile.user.role === 'admin' ? '👑 অ্যাডমিন' : '👷 ওয়ার্কার'} · {profile.user.phone || profile.user.email || 'যোগাযোগ নেই'}
                </p>
                <div className="mt-1 flex items-center gap-2">
                  <Badge variant={profile.user.is_active ? 'success' : 'neutral'}>
                    {profile.user.is_active ? 'সক্রিয়' : 'নিষ্ক্রিয়'}
                  </Badge>
                  <span className="text-xs text-ink-500">দৈনিক: {fmtBDT(profile.user.daily_salary)}</span>
                </div>
              </div>
            </div>

            {/* Stats grid */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              {(() => {
                const s = profile.stats;
                const cards = [
                  { key: 'present',  label: 'কাজের দিন',   icon: '✓', value: s.present_days, color: 'brand',   sub: `${s.half_days} আধা দিন` },
                  { key: 'gross',    label: 'মোট আয়',      icon: '💰', value: fmtBDT(s.total_gross), color: 'emerald', sub: `${s.effective_days.toFixed(1)} effective` },
                  { key: 'paid',     label: 'পরিশোধিত',    icon: '💸', value: fmtBDT(s.total_paid + s.total_partial_paid), color: 'brand', sub: `${s.salary_records} রেকর্ড` },
                  { key: 'baki',     label: 'বকেয়া',       icon: '⏳', value: fmtBDT(s.baki), color: s.baki > 0 ? 'amber' : 'emerald', sub: 'pending' },
                ];
                return cards.map((c) => (
                  <div key={c.key} className={
                    'p-3 rounded-2xl border ' +
                    (c.color === 'brand'   ? 'bg-brand-50 dark:bg-brand-900/20 border-brand-200 dark:border-brand-800' :
                     c.color === 'emerald' ? 'bg-emerald-50 dark:bg-emerald-900/20 border-emerald-200 dark:border-emerald-800' :
                     c.color === 'amber'   ? 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800' :
                                              'bg-ink-50 dark:bg-ink-900/30 border-ink-200 dark:border-ink-700')
                  }>
                    <div className="flex items-center justify-between">
                      <span className="text-xl">{c.icon}</span>
                    </div>
                    <div className="mt-1">
                      <p className="text-lg font-bold text-ink-900 dark:text-white">{c.value}</p>
                      <p className="text-[10px] text-ink-500 uppercase tracking-wider mt-0.5">{c.label}</p>
                      <p className="text-[10px] text-ink-400">{c.sub}</p>
                    </div>
                  </div>
                ));
              })()}
            </div>

            {/* Advance breakdown */}
            <div className="card p-4 bg-violet-50 dark:bg-violet-900/20 border-violet-200 dark:border-violet-800">
              <div className="flex items-center gap-2 mb-2">
                <HiOutlineCash className="w-5 h-5 text-violet-600" />
                <h4 className="text-sm font-semibold text-violet-900 dark:text-violet-200">অগ্রিম বিস্তারিত</h4>
              </div>
              <div className="grid grid-cols-3 gap-3 text-xs">
                <div>
                  <p className="text-violet-700 dark:text-violet-300 font-bold text-lg">{fmtBDT(profile.stats.advance_approved_total)}</p>
                  <p className="text-violet-600/70">অনুমোদিত</p>
                </div>
                <div>
                  <p className="text-violet-700 dark:text-violet-300 font-bold text-lg">{fmtBDT(profile.stats.advance_pending_total)}</p>
                  <p className="text-violet-600/70">বিচারাধীন</p>
                </div>
                <div>
                  <p className="text-violet-700 dark:text-violet-300 font-bold text-lg">{fmtBDT(profile.stats.advance_rejected_total)}</p>
                  <p className="text-violet-600/70">প্রত্যাখ্যাত</p>
                </div>
              </div>
              <p className="text-xs text-violet-700/70 mt-2 pt-2 border-t border-violet-200/50">
                বেতন থেকে কর্তন: <strong>{fmtBDT(profile.stats.total_advance_deducted)}</strong>
              </p>
            </div>

            {/* Salary history */}
            <div>
              <h4 className="text-sm font-semibold text-ink-900 dark:text-white mb-2 flex items-center gap-2">
                <HiOutlineCalendar className="w-5 h-5 text-accent-600" />
                বেতনের ইতিহাস ({profile.salaries.length} টি)
              </h4>
              {profile.salaries.length === 0 ? (
                <p className="text-xs text-ink-500 text-center py-4">কোনো বেতন রেকর্ড নেই।</p>
              ) : (
                <div className="border border-ink-100 dark:border-ink-700 rounded-xl overflow-hidden max-h-64 overflow-y-auto">
                  <table className="w-full text-xs">
                    <thead className="bg-ink-50 dark:bg-ink-900/30 sticky top-0">
                      <tr>
                        <th className="px-3 py-2 text-left font-semibold uppercase tracking-wider text-ink-500">সপ্তাহ</th>
                        <th className="px-3 py-2 text-center font-semibold uppercase tracking-wider text-ink-500">দিন</th>
                        <th className="px-3 py-2 text-right font-semibold uppercase tracking-wider text-ink-500">মোট</th>
                        <th className="px-3 py-2 text-right font-semibold uppercase tracking-wider text-ink-500">অগ্রিম</th>
                        <th className="px-3 py-2 text-right font-semibold uppercase tracking-wider text-ink-500">নিট</th>
                        <th className="px-3 py-2 text-center font-semibold uppercase tracking-wider text-ink-500">Status</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-ink-100 dark:divide-ink-700">
                      {profile.salaries.map((s) => (
                        <tr key={s.id} className="hover:bg-ink-50 dark:hover:bg-ink-900/30">
                          <td className="px-3 py-1.5 whitespace-nowrap">{s.week_start} → {s.week_end}</td>
                          <td className="px-3 py-1.5 text-center">{Number(s.total_days).toFixed(1)}</td>
                          <td className="px-3 py-1.5 text-right">{fmtBDT(s.gross_amount)}</td>
                          <td className="px-3 py-1.5 text-right text-violet-600">{Number(s.total_advance) > 0 ? `−${fmtBDT(s.total_advance)}` : '—'}</td>
                          <td className="px-3 py-1.5 text-right font-bold text-brand-700 dark:text-brand-300">{fmtBDT(s.net_amount)}</td>
                          <td className="px-3 py-1.5 text-center">
                            <Badge variant={STATUS_VARIANT[s.status]}>{STATUS_LABEL[s.status]}</Badge>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Attendance history */}
            <div>
              <h4 className="text-sm font-semibold text-ink-900 dark:text-white mb-2 flex items-center gap-2">
                <HiOutlineUser className="w-5 h-5 text-brand-600" />
                হাজিরার ইতিহাস (সর্বশেষ {profile.attendance.length} টি)
              </h4>
              {profile.attendance.length === 0 ? (
                <p className="text-xs text-ink-500 text-center py-4">কোনো হাজিরা নেই।</p>
              ) : (
                <div className="border border-ink-100 dark:border-ink-700 rounded-xl overflow-hidden max-h-48 overflow-y-auto">
                  <div className="divide-y divide-ink-100 dark:divide-ink-700">
                    {profile.attendance.slice(0, 30).map((a) => (
                      <div key={a.id} className="flex items-center gap-2 px-3 py-1.5 text-xs">
                        <span className="font-mono text-ink-700 dark:text-ink-300 whitespace-nowrap">{a.date}</span>
                        <span className="text-ink-500 dark:text-ink-400 truncate">{a.site?.name || '—'}</span>
                        <Badge variant={STATUS_VARIANT[a.status]} className="ml-auto">{STATUS_LABEL[a.status]}</Badge>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Advances history */}
            {profile.advances.length > 0 && (
              <div>
                <h4 className="text-sm font-semibold text-ink-900 dark:text-white mb-2 flex items-center gap-2">
                  <HiOutlineCash className="w-5 h-5 text-violet-600" />
                  অগ্রিমের ইতিহাস ({profile.advances.length} টি)
                </h4>
                <div className="border border-ink-100 dark:border-ink-700 rounded-xl overflow-hidden max-h-48 overflow-y-auto">
                  <div className="divide-y divide-ink-100 dark:divide-ink-700">
                    {profile.advances.map((a) => (
                      <div key={a.id} className="flex items-center gap-2 px-3 py-1.5 text-xs">
                        <span className="font-mono text-ink-700 dark:text-ink-300 whitespace-nowrap">{a.created_at?.split('T')[0]}</span>
                        <span className="font-bold text-violet-700 dark:text-violet-300 whitespace-nowrap">{fmtBDT(a.amount)}</span>
                        <span className="text-ink-500 dark:text-ink-400 truncate flex-1">{a.reason || '—'}</span>
                        <Badge variant={STATUS_VARIANT[a.status]}>{STATUS_LABEL[a.status]}</Badge>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Footer actions */}
            <div className="flex justify-between items-center pt-3 border-t border-ink-100 dark:border-ink-700">
              <div className="flex gap-2">
                <button
                  onClick={() => toggleActive(profile.user)}
                  className={
                    'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold border transition-colors ' +
                    (profile.user.is_active
                      ? 'bg-amber-50 text-amber-700 border-amber-200 hover:bg-amber-100 dark:bg-amber-900/30 dark:text-amber-200 dark:border-amber-800'
                      : 'bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100 dark:bg-emerald-900/30 dark:text-emerald-200 dark:border-emerald-800')
                  }
                >
                  {profile.user.is_active ? <HiOutlineBan className="w-4 h-4" /> : <HiOutlineShieldCheck className="w-4 h-4" />}
                  {profile.user.is_active ? 'নিষ্ক্রিয় করুন' : 'সক্রিয় করুন'}
                </button>
              </div>
              <button onClick={() => setProfileOpen(false)} className="btn-secondary">বন্ধ</button>
            </div>
          </div>
        )}
      </Modal>

      {/* ── WhatsApp Report Modal ─────────────────────────────────────── */}
      <Modal
        isOpen={reportOpen}
        onClose={() => setReportOpen(false)}
        size="lg"
        title="📊 পারফরম্যান্স রিপোর্ট"
      >
        {reportLoading ? (
          <div className="flex items-center justify-center py-12">
            <div className="w-10 h-10 border-4 border-brand-200 border-t-brand-600 rounded-full animate-spin" />
          </div>
        ) : !report ? (
          <p className="text-center text-ink-500 py-8">রিপোর্ট লোড করা যায়নি</p>
        ) : (
          <div className="space-y-4">
            {/* Worker header */}
            <div className="flex items-center gap-3 p-3 rounded-xl bg-gradient-to-br from-emerald-50 to-brand-50 dark:from-emerald-900/20 dark:to-brand-900/20">
              <div className="w-12 h-12 rounded-2xl bg-gradient-brand flex items-center justify-center text-white font-bold text-lg">
                {report.worker.name?.charAt(0)}
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-bold text-ink-900 dark:text-white truncate">{report.worker.name}</p>
                <p className="text-xs text-ink-500 dark:text-ink-400">{report.worker.phone} · ৳{report.worker.daily_salary}/দিন</p>
              </div>
              <select
                value={reportPeriod}
                onChange={(e) => openReport({ id: report.worker.id }, e.target.value)}
                className="text-xs px-2 py-1 rounded-md border border-ink-200 dark:border-ink-700 bg-white dark:bg-ink-800"
              >
                <option value="4w">শেষ ৪ সপ্তাহ</option>
                <option value="month">শেষ ৩০ দিন</option>
                <option value="all">সব সময়</option>
              </select>
            </div>

            <p className="text-xs text-ink-500 text-center -mt-2">📅 {report.period.from} → {report.period.to}</p>

            {/* Attendance */}
            <div className="grid grid-cols-3 gap-2">
              <div className="p-3 rounded-xl bg-emerald-50 dark:bg-emerald-900/20 text-center">
                <p className="text-[10px] uppercase tracking-wider text-emerald-700 dark:text-emerald-300">প্রেজেন্ট</p>
                <p className="font-bold text-xl text-emerald-700 dark:text-emerald-300">{report.attendance.present}</p>
              </div>
              <div className="p-3 rounded-xl bg-amber-50 dark:bg-amber-900/20 text-center">
                <p className="text-[10px] uppercase tracking-wider text-amber-700">হাফ</p>
                <p className="font-bold text-xl text-amber-700">{report.attendance.half}</p>
              </div>
              <div className="p-3 rounded-xl bg-rose-50 dark:bg-rose-900/20 text-center">
                <p className="text-[10px] uppercase tracking-wider text-rose-700">অনুপস্থিত</p>
                <p className="font-bold text-xl text-rose-700">{report.attendance.absent}</p>
              </div>
            </div>

            {/* Sites */}
            {report.attendance.sites.length > 0 && (
              <div className="space-y-1.5">
                <p className="text-[11px] font-bold uppercase tracking-wider text-ink-500">📍 সাইট</p>
                {report.attendance.sites.map((s, i) => (
                  <div key={i} className="flex items-center justify-between p-2 rounded-lg bg-ink-50 dark:bg-ink-900/30">
                    <span className="text-sm font-medium text-ink-700 dark:text-ink-200">{s.name}</span>
                    <span className="text-xs font-bold text-brand-600">{s.days} দিন</span>
                  </div>
                ))}
              </div>
            )}

            {/* Salary */}
            <div className="grid grid-cols-2 gap-2">
              <div className="p-3 rounded-xl bg-brand-50 dark:bg-brand-900/20">
                <p className="text-[10px] uppercase tracking-wider text-brand-700">মোট আয়</p>
                <p className="font-bold text-base text-brand-700 dark:text-brand-300">৳{Number(report.salary.gross).toLocaleString('en-IN')}</p>
              </div>
              <div className="p-3 rounded-xl bg-rose-50 dark:bg-rose-900/20">
                <p className="text-[10px] uppercase tracking-wider text-rose-700">বকেয়া</p>
                <p className="font-bold text-base text-rose-700 dark:text-rose-300">৳{Number(report.salary.pending).toLocaleString('en-IN')}</p>
              </div>
              <div className="p-3 rounded-xl bg-emerald-50 dark:bg-emerald-900/20">
                <p className="text-[10px] uppercase tracking-wider text-emerald-700">পরিশোধিত</p>
                <p className="font-bold text-base text-emerald-700 dark:text-emerald-300">৳{Number(report.salary.paid).toLocaleString('en-IN')}</p>
              </div>
              <div className="p-3 rounded-xl bg-violet-50 dark:bg-violet-900/20">
                <p className="text-[10px] uppercase tracking-wider text-violet-700">অগ্রিম</p>
                <p className="font-bold text-base text-violet-700 dark:text-violet-300">৳{Number(report.salary.advance).toLocaleString('en-IN')}</p>
              </div>
            </div>

            {/* Current week estimate */}
            <div className="p-3 rounded-xl bg-gradient-to-r from-accent-500 to-brand-500 text-white">
              <p className="text-[10px] uppercase tracking-wider text-white/80">এই সপ্তাহের আনুমানিক বাকি</p>
              <p className="font-bold text-2xl">৳{Number(report.current_week_estimate).toLocaleString('en-IN')}</p>
            </div>

            {/* Message preview */}
            <details className="bg-ink-50 dark:bg-ink-900/30 rounded-xl overflow-hidden">
              <summary className="px-3 py-2 text-xs font-bold cursor-pointer text-ink-600 dark:text-ink-300">📝 WhatsApp message preview</summary>
              <pre className="px-3 pb-3 text-[11px] whitespace-pre-wrap font-mono text-ink-700 dark:text-ink-200">{buildWhatsAppMessage(report)}</pre>
            </details>

            {/* Actions */}
            <div className="flex gap-2 pt-2">
              <button onClick={() => setReportOpen(false)} className="btn-secondary flex-1">বন্ধ</button>
              <button onClick={() => shareToWhatsApp(report)} className="btn-primary flex-1 inline-flex items-center justify-center gap-1.5 bg-emerald-600 hover:bg-emerald-700">
                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                WhatsApp এ পাঠান
              </button>
            </div>
          </div>
        )}
      </Modal>

      {/* ── Delete confirmation modal ─────────────────────────────────────── */}
      <Modal
        isOpen={!!confirmDelete}
        onClose={() => { setConfirmDelete(null); setDeleteText(''); }}
        title={confirmDelete?.kind === 'hard' ? '⚠️ সম্পূর্ণ মুছে ফেলুন' : 'নিষ্ক্রিয় করুন'}
      >
        {confirmDelete && (
          <div className="space-y-4">
            <div className="p-4 bg-rose-50 dark:bg-rose-900/30 border border-rose-200 dark:border-rose-800 rounded-xl">
              <div className="flex items-start gap-2">
                <HiOutlineExclamation className="w-5 h-5 text-rose-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-bold text-rose-800 dark:text-rose-200 text-sm">
                    {confirmDelete.kind === 'hard'
                      ? `এই ইউজার "${confirmDelete.worker.name}" সম্পূর্ণ মুছে যাবে!`
                      : `"${confirmDelete.worker.name}" নিষ্ক্রিয় করবেন?`}
                  </p>
                  <p className="text-xs text-rose-700 dark:text-rose-300 mt-1">
                    {confirmDelete.kind === 'hard'
                      ? 'হাজিরা, বেতন, অগ্রিম — সব রেকর্ড মুছে যাবে। এই কাজ ফিরিয়ে আনা যাবে না!'
                      : 'পরে আবার সক্রিয় করতে পারবেন।'}
                  </p>
                </div>
              </div>
            </div>
            {confirmDelete.kind === 'hard' && (
              <div>
                <label className="input-label">
                  নিশ্চিত করতে ইউজারের নাম লিখুন: <strong className="text-rose-700">{confirmDelete.worker.name}</strong>
                </label>
                <input
                  type="text"
                  value={deleteText}
                  onChange={(e) => setDeleteText(e.target.value)}
                  className="input-field"
                  placeholder={confirmDelete.worker.name}
                />
              </div>
            )}
            <div className="flex justify-end gap-2 pt-3 border-t border-ink-100 dark:border-ink-700">
              <button onClick={() => { setConfirmDelete(null); setDeleteText(''); }} className="btn-secondary">বাতিল</button>
              <button
                onClick={performDelete}
                disabled={deleting || (confirmDelete.kind === 'hard' && deleteText.trim() !== confirmDelete.worker.name.trim())}
                className={
                  confirmDelete.kind === 'hard'
                    ? 'inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-rose-600 hover:bg-rose-700 text-white text-sm font-semibold disabled:opacity-50 disabled:cursor-not-allowed transition-colors'
                    : 'inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-amber-600 hover:bg-amber-700 text-white text-sm font-semibold disabled:opacity-50 transition-colors'
                }
              >
                {deleting ? <HiOutlineRefresh className="w-4 h-4 animate-spin" /> : <HiOutlineTrash className="w-4 h-4" />}
                {confirmDelete.kind === 'hard' ? 'হ্যাঁ, সম্পূর্ণ মুছুন' : 'নিষ্ক্রিয় করুন'}
              </button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default WorkersPage;